type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

type JsonStorageOptions<T> = {
  key: string
  fallback: T
  storage?: StorageLike | null
}

import { usePostHog } from '@/shared/composables/usePostHog'

const { posthog } = usePostHog()

const resolveStorage = (storage?: StorageLike | null) => {
  if (storage) return storage
  if (typeof window === 'undefined') return null
  return window.localStorage
}

export const readStorage = (key: string, fallback: string | null = null, storage?: StorageLike | null) => {
  const target = resolveStorage(storage)
  if (!target) return fallback

  try {
    const value = target.getItem(key)
    return value ?? fallback
  } catch (error) {
    console.error('[Storage] 读取失败:', error)
    posthog.capture('storage_error', {
      operation: 'read',
      storageType: 'localStorage',
      error: String(error),
    })
    return fallback
  }
}

export const writeStorage = (key: string, value: string, storage?: StorageLike | null) => {
  const target = resolveStorage(storage)
  if (!target) return

  try {
    target.setItem(key, value)
  } catch (error) {
    console.error('[Storage] 写入失败:', error)
    posthog.capture('storage_error', {
      operation: 'write',
      storageType: 'localStorage',
      error: String(error),
    })
    // Ignore storage quota and private-mode failures.
  }
}

export const removeStorage = (key: string, storage?: StorageLike | null) => {
  const target = resolveStorage(storage)
  if (!target) return

  try {
    target.removeItem(key)
  } catch (error) {
    console.error('[Storage] 删除失败:', error)
    posthog.capture('storage_error', {
      operation: 'remove',
      storageType: 'localStorage',
      error: String(error),
    })
    // Ignore removal failures.
  }
}

export const readJsonStorage = <T>({ key, fallback, storage }: JsonStorageOptions<T>) => {
  const raw = readStorage(key, null, storage)
  if (!raw) return fallback

  try {
    return JSON.parse(raw) as T
  } catch (error) {
    console.error('[Storage] JSON 解析失败:', error)
    posthog.capture('storage_error', {
      operation: 'json_parse',
      storageType: 'localStorage',
      error: String(error),
    })
    return fallback
  }
}

export const writeJsonStorage = <T>(key: string, value: T, storage?: StorageLike | null) => {
  try {
    writeStorage(key, JSON.stringify(value), storage)
  } catch (error) {
    console.error('[Storage] JSON 序列化失败:', error)
    posthog.capture('storage_error', {
      operation: 'json_stringify',
      storageType: 'localStorage',
      error: String(error),
    })
    // Ignore serialization failures.
  }
}
